(function () {
  'use strict';

  class ItemBuilder extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

      const productEl = this.querySelector('[data-mh-product]');
      const configEl = this.querySelector('[data-mh-config]');
      if (!productEl || !configEl) return;

      try {
        this.product = JSON.parse(productEl.textContent);
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('JSON parse error', e);
        return;
      }

      if (!this.product || !Array.isArray(this.product.variants) || !this.product.variants.length) {
        console.warn('[MorganHeadgear] No variants');
        return;
      }

      this.sizeIdx = this.config.option_index || 1;        // card 1 (Option)
      this.shellIdx = this.config.size_option_index || 2;  // card 2 (Size)

      this.productMinPrice = this.calcProductMinPrice();

      this.state = { size: null, shell: null, strap: null, quantity: 1, slideIndex: 0 };

      this.canvasMode = this.config.preview_mode === 'canvas'
        && Array.isArray(this.config.canvas_views)
        && this.config.canvas_views.length > 0;
      this._canvas = null;
      this._optionMap = null;
      // Variants beyond Shopify's 250-per-product Liquid cap, fetched on demand by ID.
      this._resolved = {};
      this._pending = {};
      this._truncated = (this.product.variants_count || 0) > this.product.variants.length;

      this.setDefaultsFromFirstVariant();
      this._reconcileSelection();
      this._initCanvasIfEnabled();
      this.buildSizeOptions();
      this.buildShellOptions();
      this.buildStrapOptions();
      this.bindEvents();
      this.syncAll();
    }

    calcProductMinPrice() {
      let min = Infinity;
      this.product.variants.forEach(v => {
        if (typeof v.price === 'number' && v.price < min) min = v.price;
      });
      return min === Infinity ? this.basePrice : min;
    }

    setDefaultsFromFirstVariant() {
      const first = this._variantFromUrl()
        || this.product.variants.find(v => v.available)
        || this.product.variants[0];
      this.state.size = this.getOptionValue(first, this.sizeIdx);
      this.state.shell = this.getOptionValue(first, this.shellIdx);
      this.state.strap = this.getOptionValue(first, this.strapIdx);
    }

    // Honour ?variant= on load so deep links select the right combination.
    _variantFromUrl() {
      let raw = null;
      try { raw = new URL(window.location.href).searchParams.get('variant'); } catch (e) { return null; }
      if (!raw) return null;
      return this.product.variants.find(v => String(v.id) === String(raw)) || null;
    }

    getOptionValue(variant, index) {
      return variant['option' + index];
    }

    imageForOptionValue(index, value) {
      const v = this.product.variants.find(v => this.getOptionValue(v, index) === value && v.featured_image);
      return v ? v.featured_image : null;
    }


    uniqueOptionValues(index) {
      const seen = new Set();
      const out = [];
      this.product.variants.forEach(v => {
        const val = this.getOptionValue(v, index);
        if (val && !seen.has(val)) {
          seen.add(val);
          out.push(val);
        }
      });
      const productOptions = this.product.options || [];
      const optDef = productOptions[index - 1];
      if (optDef && Array.isArray(optDef.values)) {
        optDef.values.forEach(val => {
          if (val && !seen.has(val)) {
            seen.add(val);
            out.push(val);
          }
        });
      }
      return out;
    }

    buildShellOptions() {
      const grid = this.querySelector('[data-shell-grid]');
      if (!grid) return;
      const values = this.uniqueOptionValues(this.shellIdx);
      grid.innerHTML = '';
      values.forEach(val => {
        const sample = this.findSampleForOption(this.shellIdx, val, ['shell_swatch_color', 'shell_label']);
        let color = sample ? this._cleanColor(sample.shell_swatch_color) : '';
        if (!color) color = this._colorNameToHex(val) || '#E5E4DE';
        const label = sample && sample.shell_label ? sample.shell_label : val.toUpperCase();
        const up = this.getUpchargeForOptionValue(this.shellIdx, val);
        const available = this.isOptionValueAvailable(this.shellIdx, val);
        const selected = val === this.state.shell;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mh-shell-swatch' + (selected ? ' selected' : '') + (available ? '' : ' unavailable');
        btn.setAttribute('data-shell', val);
        btn.innerHTML =
          '<span class="mh-shell-dot" style="background-color:' + this.escape(color) + '">' +
          (up > 0 ? '<span class="mh-swatch-up">+' + this.formatMoney(up) + '</span>' : '') +
          this.backorderTag(this.shellIdx, val) +
          '</span>' +
          '<span class="mh-shell-name">' + this.escape(label) + '</span>';
        btn.addEventListener('click', () => this.selectShell(val));
        grid.appendChild(btn);
      });
    }

    getMinPriceForOptionValue(index, value) {
      let min = Infinity;
      this.product.variants.forEach(v => {
        if (this.getOptionValue(v, index) === value && typeof v.price === 'number' && v.price < min) {
          min = v.price;
        }
      });
      return min === Infinity ? this.productMinPrice : min;
    }

    getUpchargeForOptionValue(index, value) {
      const p = this.getMinPriceForOptionValue(index, value);
      return Math.max(0, p - this.productMinPrice);
    }

    findVariantForValue(index, value) {
      return this.product.variants.find(v => this.getOptionValue(v, index) === value) || null;
    }

    findSampleForOption(index, value, requiredFields) {
      const match = this.product.variants.find(v => {
        if (this.getOptionValue(v, index) !== value) return false;
        return requiredFields.some(f => v[f]);
      });
      return match || this.findVariantForValue(index, value);
    }

    findCurrentVariant() {
      const norm = (s) => String(s == null ? '' : s).trim();
      const size = norm(this.state.size);
      const shell = norm(this.state.shell);
      const strap = norm(this.state.strap);

      const exact = this.product.variants.find(v =>
        norm(this.getOptionValue(v, this.sizeIdx)) === size &&
        norm(this.getOptionValue(v, this.shellIdx)) === shell &&
        norm(this.getOptionValue(v, this.strapIdx)) === strap
      );
      if (exact) return exact;

      const predicted = this._predictVariant(size, shell, strap);
      if (!predicted) return null;
      const real = this._resolved[predicted.id];
      if (real) return real;
      this._lookupVariant(predicted.id);
      return predicted;
    }

    // Fetch one variant beyond the 250 cap through the Section Rendering API.
    _lookupVariant(id) {
      if (!id || !this.product.lookup_url || this._resolved[id] || this._pending[id]) return;
      this._pending[id] = true;
      const url = this.product.lookup_url + '&variant=' + encodeURIComponent(id);
      fetch(url, { headers: { 'Accept': 'text/html' } })
        .then(r => r.ok ? r.text() : Promise.reject(new Error('HTTP ' + r.status)))
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const text = (doc.body ? doc.body.textContent : html).trim();
          const data = JSON.parse(text);
          if (data && data.id) {
            this._resolved[data.id] = data;
          } else {
            // Shopify says this ID does not exist: never sellable.
            this._resolved[id] = { id: id, available: false, _missing: true };
          }
        })
        .catch(e => {
          console.warn('[MorganHeadgear] variant lookup failed', id, e);
        })
        .finally(() => {
          delete this._pending[id];
          const current = this._predictVariant(this.state.size, this.state.shell, this.state.strap);
          if (current && current.id === id) {
            this.buildStrapOptions();
            this.syncAll();
          }
        });
    }
    

    _buildOptionMap() {
      if (this._optionMap) return this._optionMap;
      this._optionMap = {
        sizes: this.uniqueOptionValues(this.sizeIdx),
        shells: this.uniqueOptionValues(this.shellIdx),
        straps: this.uniqueOptionValues(this.strapIdx)
      };
      return this._optionMap;
    }

    _predictVariant(size, shell, strap) {
      const map = this._buildOptionMap();
      const sizeIdx = map.sizes.indexOf(size);
      const shellIdx = map.shells.indexOf(shell);
      const strapIdx = map.straps.indexOf(strap);
      if (sizeIdx < 0 || shellIdx < 0 || strapIdx < 0) return null;

      const variants = this.product.variants;
      if (!variants.length) return null;

      const baseId = variants[0].id;
      const increment = variants.length >= 2
        ? Math.round((variants[variants.length - 1].id - variants[0].id) / (variants.length - 1))
        : 32768;

      const shellCount = map.shells.length;
      const strapCount = map.straps.length;
      const position = sizeIdx * shellCount * strapCount + shellIdx * strapCount + strapIdx + 1;
      const predictedId = baseId + (position - 1) * increment;

      const norm = (s) => String(s == null ? '' : s).trim();
      const sameShellAndStrap = variants.find(v =>
        norm(this.getOptionValue(v, this.shellIdx)) === shell &&
        norm(this.getOptionValue(v, this.strapIdx)) === strap
      );
      const sameShell = variants.find(v =>
        norm(this.getOptionValue(v, this.sizeIdx)) === size &&
        norm(this.getOptionValue(v, this.shellIdx)) === shell
      );
      const sameStrapOnly = variants.find(v =>
        norm(this.getOptionValue(v, this.strapIdx)) === strap &&
        (v.slider_image_1 || v.slider_image_2 || v.featured_image)
      );
      const sameSize = variants.find(v =>
        norm(this.getOptionValue(v, this.sizeIdx)) === size
      );
      const sampleShell = this.findVariantForValue(this.shellIdx, shell);
      const sampleStrap = this.findVariantForValue(this.strapIdx, strap);
      const sourceForMeta = sameShellAndStrap || sameShell || sameStrapOnly || sameSize || variants[0];

      const strapPriceRef = this.findVariantForValue(this.strapIdx, strap);
      const shellPriceRef = this.findVariantForValue(this.shellIdx, shell);
      const priceEst = Math.max(
        strapPriceRef ? strapPriceRef.price : 0,
        shellPriceRef ? shellPriceRef.price : 0,
        sourceForMeta.price || 0
      );

      const result = {
        id: predictedId,
        available: true,
        price: priceEst,
        compare_at_price: sourceForMeta.compare_at_price || 0,
        featured_image: sourceForMeta.featured_image || null,
        slider_image_1: sourceForMeta.slider_image_1 || null,
        slider_image_2: sourceForMeta.slider_image_2 || null,
        shell_swatch_color: sampleShell ? sampleShell.shell_swatch_color : '',
        strap_swatch_color: sampleStrap ? sampleStrap.strap_swatch_color : '',
        strap_swatch_image: sampleStrap ? sampleStrap.strap_swatch_image : null,
        size_fit: sourceForMeta.size_fit || null,
        shell_label: sampleShell ? sampleShell.shell_label : null,
        _predicted: true
      };
      result['option' + this.sizeIdx] = size;
      result['option' + this.shellIdx] = shell;
      result['option' + this.strapIdx] = strap;
      return result;
    }

    // An option value is "backorder only" when nothing with that value is in stock
    // but at least one variant with it can be bought on backorder.
    backorderInfoForOption(index, value) {
      const norm = (s) => String(s == null ? '' : s).trim();
      const constraints = [];
      if (index !== this.sizeIdx) constraints.push([this.sizeIdx, norm(this.state.size)]);
      if (index === this.strapIdx) constraints.push([this.shellIdx, norm(this.state.shell)]);
      let inStock = false;
      let bo = null;
      this.product.variants.forEach(v => {
        if (norm(this.getOptionValue(v, index)) !== norm(value)) return;
        if (!constraints.every(([ci, cv]) => !cv || norm(this.getOptionValue(v, ci)) === cv)) return;
        if (v.available && !v.backorder) inStock = true;
        if (v.backorder && !bo) bo = v;
      });
      if (inStock || !bo) return null;
      return { date: bo.ships_by || '' };
    }

    backorderTag(index, value) {
      const info = this.backorderInfoForOption(index, value);
      if (!info) return '';
      const strings = (this.config && this.config.strings) || {};
      const text = info.date
        ? (strings.backorder_ships_short || 'Ships [date]').replace('[date]', info.date)
        : (strings.backorder || 'Backorder');
      return '<span class="mh-swatch-backorder">' + this.escape(text) + '</span>';
    }

    // Availability is relative to what is already selected, the way Shopify's own
    // option picker works: a size is available if anything in that size is in stock,
    // a shell if that size + shell has stock, a strap if the full combination does.
    // Backordered variants count as available (they can be bought).
    isOptionValueAvailable(index, value) {
      const norm = (s) => String(s == null ? '' : s).trim();
      const constraints = [];
      if (index !== this.sizeIdx) constraints.push([this.sizeIdx, norm(this.state.size)]);
      if (index === this.strapIdx) constraints.push([this.shellIdx, norm(this.state.shell)]);
      const matches = this.product.variants.filter(v =>
        norm(this.getOptionValue(v, index)) === norm(value) &&
        constraints.every(([ci, cv]) => !cv || norm(this.getOptionValue(v, ci)) === cv)
      );
      if (matches.some(v => v.available)) return true;
      if (matches.length) return false;

      // Nothing in the first 250 variants matches. If the product has more variants
      // than Liquid exposes, the combination probably exists but is unknown here.
      if (!this._truncated) return false;
      if (index === this.strapIdx) {
        // Full combination: use the fetched answer when we have one.
        const predicted = this._predictVariant(norm(this.state.size), norm(this.state.shell), norm(value));
        const real = predicted && this._resolved[predicted.id];
        if (real) return !!real.available;
      }
      return true;
    }

    // After a selection changes, move any now-impossible choice to the first
    // available one so the visible state always describes a real, buyable variant.
    _reconcileSelection() {
      const firstAvailable = (index) => this.uniqueOptionValues(index).find(val => this.isOptionValueAvailable(index, val));
      if (this.state.shell && !this.isOptionValueAvailable(this.shellIdx, this.state.shell)) {
        const shell = firstAvailable(this.shellIdx);
        if (shell) this.state.shell = shell;
      }
      if (this.state.strap && !this.isOptionValueAvailable(this.strapIdx, this.state.strap)) {
        const strap = firstAvailable(this.strapIdx);
        if (strap) this.state.strap = strap;
      }
    }

    

    buildSizeOptions() {
      const grid = this.querySelector('[data-size-grid]');
      if (!grid) return;
      const values = this.uniqueOptionValues(this.sizeIdx);
      grid.innerHTML = '';
      values.forEach(val => {
        const currentImg = this.imageForOptionValue(this.sizeIdx, val);
        const sample = this.findSampleForOption(this.sizeIdx, val, ['size_fit']);
        const fit = sample && sample.size_fit ? sample.size_fit : '';
        const up = this.getUpchargeForOptionValue(this.sizeIdx, val);
        const available = this.isOptionValueAvailable(this.sizeIdx, val);
        const selected = val === this.state.size;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mh-size-option' + (selected ? ' selected' : '') + (available ? '' : ' unavailable') + (currentImg ? ' has-img' : '');
        btn.setAttribute('data-size', val);
        btn.innerHTML =
          '<div class="mh-check"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></div>' +
          (currentImg ? '<div class="mh-size-img"><img src="' + currentImg + '" alt="' + this.escape(val) + '" loading="lazy"></div>' : '') +
          '<div class="mh-size-text">' +
            '<div class="mh-size-name">' + this.escape(val) + '</div>' +
            (fit ? '<div class="mh-size-fit">' + this.escape(fit) + '</div>' : '') +
            (up > 0 ? '<div class="mh-size-up">+' + this.formatMoney(up) + '</div>' : '') +
            this.backorderTag(this.sizeIdx, val) +
          '</div>';
        btn.addEventListener('click', () => this.selectSize(val));
        grid.appendChild(btn);
      });
    }

    _cleanColor(input) {
      if (!input) return '';
      if (typeof input === 'string') return input.trim();
      if (typeof input === 'object' && input.value) return String(input.value).trim();
      return '';
    }

    _cleanImageUrl(input) {
      if (!input) return '';
      if (typeof input === 'string') return input.trim();
      if (typeof input === 'object' && input.value) return String(input.value).trim();
      return '';
    }


    buildStrapOptions() {
      const solidGrid = this.querySelector('[data-strap-solid-grid]');
      const printGrid = this.querySelector('[data-strap-print-grid]');
      const printWrap = this.querySelector('[data-strap-printed-wrap]');
      if (!solidGrid) return;

      const values = this.uniqueOptionValues(this.strapIdx);
      const solids = [];
      const prints = [];

      values.forEach(val => {
        const sample = this.findSampleForOption(this.strapIdx, val, ['strap_swatch_color', 'strap_swatch_image']);
        const up = this.getUpchargeForOptionValue(this.strapIdx, val);
        const available = this.isOptionValueAvailable(this.strapIdx, val);
        const rawColor = sample ? this._cleanColor(sample.strap_swatch_color) : '';
        const rawImage = sample ? this._cleanImageUrl(sample.strap_swatch_image) : '';
        const isPrinted = !!rawImage;
        const fallbackColor = rawColor || this._colorNameToHex(val) || '#CCCCCC';
        const item = {
          value: val,
          color: fallbackColor,
          image: rawImage || null,
          upcharge: up,
          available: available
        };
        if (isPrinted) prints.push(item); else solids.push(item);
      });

      const self = this;
      // When the whole size + shell is backordered the shell swatch and the note already
      // say so; tagging every strap would read as if the straps themselves were out.
      const shellBackordered = !!this.backorderInfoForOption(this.shellIdx, this.state.shell);
      const renderStrap = (item) => {
        const selected = item.value === this.state.strap;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mh-strap-swatch' + (selected ? ' selected' : '') + (item.available ? '' : ' unavailable');
        btn.setAttribute('data-strap', item.value);
        const dotStyle = item.image
          ? 'background-image:url(\'' + item.image + '\');background-size:cover;background-position:center;background-repeat:no-repeat;background-color:' + self.escape(item.color)
          : 'background-color:' + self.escape(item.color);
        btn.innerHTML =
          '<span class="mh-strap-dot" style="' + dotStyle + '">' +
          (item.upcharge > 0 ? '<span class="mh-swatch-up">+' + this.formatMoney(item.upcharge) + '</span>' : '') +
          (shellBackordered ? '' : self.backorderTag(self.strapIdx, item.value)) +
          '</span>' +
          '<span class="mh-strap-name">' + this.escape(item.value) + '</span>';
        btn.addEventListener('click', () => this.selectStrap(item.value));
        return btn;
      };

      solidGrid.innerHTML = '';
      solids.forEach(item => solidGrid.appendChild(renderStrap(item)));

      if (printWrap) {
        if (prints.length && printGrid) {
          printWrap.hidden = false;
          printGrid.innerHTML = '';
          prints.forEach(item => printGrid.appendChild(renderStrap(item)));
        } else {
          printWrap.hidden = true;
        }
      }
    }

    bindEvents() {
      const qtyUp = this.querySelector('[data-qty-up]');
      const qtyDown = this.querySelector('[data-qty-down]');
      if (qtyUp) qtyUp.addEventListener('click', () => this.changeQty(1));
      if (qtyDown) qtyDown.addEventListener('click', () => this.changeQty(-1));

      const heroPrev = this.querySelector('[data-hero-prev]');
      const heroNext = this.querySelector('[data-hero-next]');
      if (heroPrev) heroPrev.addEventListener('click', () => {
        if (this.canvasMode && this._canvas) this._canvasPrev();
        else this.goToPrevSlide();
      });
      if (heroNext) heroNext.addEventListener('click', () => {
        if (this.canvasMode && this._canvas) this._canvasNext();
        else this.goToNextSlide();
      });

      const inline = this.querySelector('[data-mh-submit]');
      if (inline) {
        inline.addEventListener('click', () => {
          this.syncAll();
        });
      }

      const sticky = this.querySelector('[data-mh-sticky-submit]');
      if (sticky) {
        sticky.addEventListener('click', () => {
          this.syncAll();
          const form = this.querySelector('form.mh-form');
          if (form && typeof form.requestSubmit === 'function') form.requestSubmit();
          else if (form) form.submit();
        });
      }

      const sizeGuideBtn = this.querySelector('[data-size-guide-open]');
      const sizeGuideModal = this.querySelector('[data-size-guide-modal]');
      const sizeGuideCloseEls = this.querySelectorAll('[data-size-guide-close]');

      if (sizeGuideBtn && sizeGuideModal) {
        sizeGuideBtn.addEventListener('click', () => {
          sizeGuideModal.hidden = false;
          document.body.classList.add('mh-modal-open');
        });
        sizeGuideCloseEls.forEach(el => {
          el.addEventListener('click', () => {
            sizeGuideModal.hidden = true;
            document.body.classList.remove('mh-modal-open');
          });
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !sizeGuideModal.hidden) {
            sizeGuideModal.hidden = true;
            document.body.classList.remove('mh-modal-open');
          }
        });
      }

      this._initQuickBuy();
    }

    _initQuickBuy() {
      const buttons = this.querySelectorAll('[data-quick-buy]');
      buttons.forEach(btn => {
        const shellName = btn.getAttribute('data-qb-shell');
        const strapName = btn.getAttribute('data-qb-strap');
        const shellPreview = btn.querySelector('[data-qb-shell-color]');
        const strapPreview = btn.querySelector('[data-qb-strap-color]');

        if (shellPreview) {
          const shellSample = this.findSampleForOption(this.shellIdx, shellName, ['shell_swatch_color']);
          let shellColor = shellSample ? this._cleanColor(shellSample.shell_swatch_color) : '';
          if (!shellColor) shellColor = this._colorNameToHex(shellName) || '#E5E4DE';
          shellPreview.style.background = shellColor;
        }
        if (strapPreview) {
          const strapSample = this.findSampleForOption(this.strapIdx, strapName, ['strap_swatch_color', 'strap_swatch_image']);
          const strapImg = strapSample ? this._cleanImageUrl(strapSample.strap_swatch_image) : '';
          let strapColor = strapSample ? this._cleanColor(strapSample.strap_swatch_color) : '';
          if (!strapColor) strapColor = this._colorNameToHex(strapName) || '#CCCCCC';
          if (strapImg) {
            strapPreview.style.backgroundImage = 'url("' + strapImg + '")';
            strapPreview.style.backgroundSize = 'cover';
            strapPreview.style.backgroundPosition = 'center';
          } else {
            strapPreview.style.background = strapColor;
          }
        }

        btn.addEventListener('click', () => {
          this.state.shell = shellName;
          this.state.strap = strapName;
          this._reconcileSelection();
          this.buildShellOptions();
          this.buildStrapOptions();
          this.syncAll();
          this._updateQuickBuySelection();
        });
      });

      const scrollBtn = this.querySelector('[data-quick-buy-scroll]');
      if (scrollBtn) {
        scrollBtn.addEventListener('click', () => {
          const shellSection = this.querySelector('[data-shell-grid]');
          if (shellSection) {
            const card = shellSection.closest('.mh-card');
            const target = card || shellSection;
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    }

    _updateQuickBuySelection() {
      const buttons = this.querySelectorAll('[data-quick-buy]');
      buttons.forEach(btn => {
        const shellName = btn.getAttribute('data-qb-shell');
        const strapName = btn.getAttribute('data-qb-strap');
        const isSelected = this.state.shell === shellName && this.state.strap === strapName;
        btn.classList.toggle('selected', isSelected);
      });
    }

    // _highlightSelectedOptions() {
    //   const selectors = ['.mh-shell-swatch.selected', '.mh-strap-swatch.selected'];
    //   selectors.forEach(sel => {
    //     const el = this.querySelector(sel);
    //     if (el) {
    //       el.classList.remove('mh-quick-buy-highlight');
    //       void el.offsetWidth;
    //       el.classList.add('mh-quick-buy-highlight');
    //       setTimeout(() => el.classList.remove('mh-quick-buy-highlight'), 1400);
    //     }
    //   });
    // }

    selectSize(val) {
      if (this.state.size === val) return;
      this.state.size = val;
      this._reconcileSelection();
      this.buildSizeOptions();
      this.buildShellOptions();
      this.buildStrapOptions();
      this.syncAll();
    }

    selectShell(val) {
      if (this.state.shell === val) return;
      this.state.shell = val;
      this._reconcileSelection();
      this.buildShellOptions();
      this.buildStrapOptions();
      this.syncAll();
    }

    selectStrap(val) {
      if (this.state.strap === val) return;
      this.state.strap = val;
      this.buildStrapOptions();
      this.syncAll();
    }

    changeQty(delta) {
      const next = Math.max(1, this.state.quantity + delta);
      this.state.quantity = next;
      const val = this.querySelector('[data-qty-value]');
      const inp = this.querySelector('[data-qty-input]');
      if (val) val.textContent = next;
      if (inp) inp.value = next;
      this.updatePrice(this.findCurrentVariant());
    }

    syncAll() {
      const variant = this.findCurrentVariant();
      this.updateVariantInput(variant);
      this.updatePrice(variant);
      this.updateBadges();
      this.updateSlider(variant);
      this._updateLineItemProps();
      this._updateUrl(variant);
      this._updateQuickBuySelection();
      this.updateBackorder(variant);
      this.updateSubmitState(variant);
      this.updateProgress();
    }

  updateProgress() {
    const done = [this.state.size, this.state.shell, this.state.strap].filter(Boolean).length;
    const pct = Math.round((done / 3) * 100);
    this.querySelectorAll('[data-mh-progress-step]').forEach(el => el.textContent = done);
    this.querySelectorAll('[data-mh-progress-percent]').forEach(el => el.textContent = pct + '%');
    this.querySelectorAll('[data-mh-progress-fill]').forEach(el => el.style.width = pct + '%');
  }

    // Backordered variant: button reads "Backorder · ships by <date>", a note explains it,
    // and the date rides along as a line item property into cart, checkout and the order.
    updateBackorder(variant) {
      const strings = (this.config && this.config.strings) || {};
      const active = !!(variant && variant.backorder);
      const date = active ? (variant.ships_by || '') : '';
      const label = active
        ? (date ? (strings.backorder_ships_by || 'Backorder · ships by [date]').replace('[date]', date)
                : (strings.backorder || 'Backorder'))
        : '';

      const inlineText = this.querySelector('[data-atb-text]');
      if (inlineText) {
        if (!inlineText.dataset.defaultText) inlineText.dataset.defaultText = inlineText.textContent;
        inlineText.textContent = active ? label : inlineText.dataset.defaultText;
      }
      const stickyText = this.querySelector('[data-mh-sticky-submit] .mh-atb-left > span');
      if (stickyText) {
        if (!stickyText.dataset.defaultText) stickyText.dataset.defaultText = stickyText.textContent;
        stickyText.textContent = active ? label : stickyText.dataset.defaultText;
      }

      const note = this.querySelector('[data-backorder-note]');
      if (note) {
        note.textContent = label;
        note.classList.toggle('is-visible', active);
      }

      const prop = this.querySelector('[data-prop-ships-by]');
      if (prop) {
        prop.value = active ? (date || (strings.backorder || 'Backorder')) : '';
        prop.disabled = !active;
      }
    }

    updateVariantInput(variant) {
      const inp = this.querySelector('[data-variant-id-input]');
      if (!inp) return;
      if (variant && variant.available !== false) {
        inp.value = variant.id;
        inp.disabled = false;
      } else {
        inp.value = '';
        inp.disabled = true;
      }
    }

    _updateLineItemProps() {
      const opt = this.state.size || '';   // card 1 (Option)
      const size = this.state.shell || ''; // card 2 (Size)
      const propOption = this.querySelector('[data-prop-option]');
      const propSize = this.querySelector('[data-prop-size]');
      if (propOption) { propOption.value = opt; propOption.disabled = !opt; }
      if (propSize) { propSize.value = size; propSize.disabled = !size; }
    }

    _updateUrl(variant) {
      if (!variant || !variant.id) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        console.warn('[MorganHeadgear] URL update failed', e);
      }
    }

    updatePrice(variant) {
      const price = variant ? variant.price : this.basePrice;
      const formatted = this.formatMoney(price);
      this.querySelectorAll('[data-price-display]').forEach(el => el.textContent = formatted);
      const sticky = this.querySelector('[data-sticky-total]');
      if (sticky) sticky.textContent = this.formatMoney(price * this.state.quantity);
    }

    updateBadges() {
      const sizeUp = this.getUpchargeForOptionValue(this.sizeIdx, this.state.size);
      const shellUp = this.getUpchargeForOptionValue(this.shellIdx, this.state.shell);
      const strapUp = this.getUpchargeForOptionValue(this.strapIdx, this.state.strap);
      this.setBadge('[data-size-badge]', this.state.size, sizeUp);
      this.setBadge('[data-shell-badge]', this.state.shell, shellUp);
      this.setBadge('[data-strap-badge]', this.state.strap, strapUp);
    }

    setBadge(selector, name, upcharge) {
      const el = this.querySelector(selector);
      if (!el) return;
      const upText = upcharge > 0 ? '<span class="mh-badge-up">' + this.formatMoney(upcharge) + '</span>' : '';
      el.innerHTML = this.escape(name || '') + upText;
    }


    _initCanvasIfEnabled() {
      if (!this.canvasMode) return;
      if (typeof window.MorganHeadgearCanvas !== 'function') {
        console.warn('[MorganHeadgear] Canvas library not loaded, falling back to slider');
        this.canvasMode = false;
        return;
      }
      const wrap = this.querySelector('[data-canvas-wrap]');
      const slider = this.querySelector('[data-hero-slider]');
      const dots = this.querySelector('[data-slider-dots]');
      if (!wrap) { this.canvasMode = false; return; }
      if (slider) slider.style.display = 'none';
      if (dots) dots.style.display = 'none';
      wrap.hidden = false;

      const patternUrls = [];
      this.product.variants.forEach(v => {
        const u = this._cleanImageUrl(v.strap_swatch_image);
        if (u && patternUrls.indexOf(u) === -1) patternUrls.push(u);
      });

      const self = this;
      this._canvas = new window.MorganHeadgearCanvas(wrap, {
        views: this.config.canvas_views,
        preloadPatterns: patternUrls,
        onReady: function () { self.updateCanvas(); }
      });

      const prevBtn = this.querySelector('[data-hero-prev]');
      const nextBtn = this.querySelector('[data-hero-next]');
      if (prevBtn && nextBtn) {
        const showButtons = (this.config.canvas_views || []).length > 1;
        prevBtn.hidden = !showButtons;
        nextBtn.hidden = !showButtons;
      }
    }

    _canvasPrev() {
      if (!this._canvas) return;
      const total = (this.config.canvas_views || []).length;
      if (total < 2) return;
      const cur = this._canvas.activeViewIndex || 0;
      const prev = (cur - 1 + total) % total;
      this._canvas.setView(prev);
    }

    _canvasNext() {
      if (!this._canvas) return;
      const total = (this.config.canvas_views || []).length;
      if (total < 2) return;
      const cur = this._canvas.activeViewIndex || 0;
      const next = (cur + 1) % total;
      this._canvas.setView(next);
    }

    _colorNameToHex(name) {
      if (!name) return '';
      const map = {
        'white': '#F5F1E8', 'black': '#111111', 'red': '#DC2626', 'off red': '#B33A3A',
        'navy': '#1E3A8A', 'navy blue': '#1E3A8A', 'royal blue': '#1E40AF',
        'light blue': '#60A5FA', 'ocean blue': '#0284C7', 'blue': '#2563EB',
        'army green': '#4B5320', 'dark green': '#166534', 'neon lime': '#A3E635',
        'green': '#16A34A', 'orange': '#F97316', 'purple': '#7C3AED',
        'pink': '#EC4899', 'gold': '#D4AF37', 'silver': '#C0C0C0',
        'gunmetal grey': '#4A5058', 'gunmetal gray': '#4A5058', 'grey': '#6B7280', 'gray': '#6B7280'
      };
      return map[String(name).toLowerCase().trim()] || '';
    }

    updateCanvas() {
      if (!this.canvasMode || !this._canvas) return;
      const shellSample = this.findSampleForOption(this.shellIdx, this.state.shell, ['shell_swatch_color']);
      const strapSample = this.findSampleForOption(this.strapIdx, this.state.strap, ['strap_swatch_color', 'strap_swatch_image']);

      let shellColor = shellSample ? this._cleanColor(shellSample.shell_swatch_color) : '';
      if (!shellColor) shellColor = this._colorNameToHex(this.state.shell) || '#F5F1E8';
      this._canvas.setShellColor(shellColor);

      const strapImg = strapSample ? this._cleanImageUrl(strapSample.strap_swatch_image) : '';
      if (strapImg) {
        this._canvas.setStrapPattern(strapImg);
      } else {
        let strapColor = strapSample ? this._cleanColor(strapSample.strap_swatch_color) : '';
        if (!strapColor) strapColor = this._colorNameToHex(this.state.strap) || '#FFFFFF';
        this._canvas.setStrapColor(strapColor);
      }
    }

    updateSlider(variant) {
      if (this.canvasMode) { this.updateCanvas(); return; }
      const slider = this.querySelector('[data-hero-slider]');
      const dotsWrap = this.querySelector('[data-slider-dots]');
      if (!slider) return;

      const images = [];
      if (variant && variant.slider_image_1) images.push(variant.slider_image_1);
      if (variant && variant.slider_image_2) images.push(variant.slider_image_2);
      if (!images.length && variant && variant.featured_image) images.push(variant.featured_image);
      if (!images.length) {
        const existing = slider.querySelector('img');
        if (existing && existing.src) images.push(existing.src);
      }

      slider.innerHTML = '';
      images.forEach((src, idx) => {
        const img = document.createElement('img');
        img.src = src;
        img.className = 'mh-hero-img' + (idx === 0 ? ' is-active' : '');
        img.setAttribute('data-hero-slide', String(idx));
        img.loading = idx === 0 ? 'eager' : 'lazy';
        img.alt = this.product.title || 'Morgan Headgear';
        slider.appendChild(img);
      });
      this.state.slideIndex = 0;

      const prevBtn = this.querySelector('[data-hero-prev]');
      const nextBtn = this.querySelector('[data-hero-next]');
      if (prevBtn && nextBtn) {
        if (images.length > 1) {
          prevBtn.hidden = false;
          nextBtn.hidden = false;
        } else {
          prevBtn.hidden = true;
          nextBtn.hidden = true;
        }
      }

      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        if (images.length > 1) {
          images.forEach((_, idx) => {
            const d = document.createElement('button');
            d.type = 'button';
            d.className = 'mh-slider-dot' + (idx === 0 ? ' is-active' : '');
            d.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
            d.addEventListener('click', () => this.goToSlide(idx));
            dotsWrap.appendChild(d);
          });
        }
      }

      this._sliderTotal = images.length;
    }

    goToPrevSlide() {
      const total = this._sliderTotal || 0;
      if (total < 2) return;
      const prev = (this.state.slideIndex - 1 + total) % total;
      this.goToSlide(prev);
    }

    goToNextSlide() {
      const total = this._sliderTotal || 0;
      if (total < 2) return;
      const next = (this.state.slideIndex + 1) % total;
      this.goToSlide(next);
    }

    goToSlide(idx) {
      const slider = this.querySelector('[data-hero-slider]');
      const dotsWrap = this.querySelector('[data-slider-dots]');
      if (!slider) return;
      slider.querySelectorAll('.mh-hero-img').forEach((s, i) => s.classList.toggle('is-active', i === idx));
      if (dotsWrap) {
        dotsWrap.querySelectorAll('.mh-slider-dot').forEach((d, i) => d.classList.toggle('is-active', i === idx));
      }
      this.state.slideIndex = idx;
    }

    updateSubmitState(variant) {
      const inline = this.querySelector('[data-mh-submit]');
      const sticky = this.querySelector('[data-mh-sticky-submit]');
      const disabled = !variant || variant.available === false;
      if (inline) inline.disabled = disabled;
      if (sticky) sticky.disabled = disabled;

      const soldOut = !!variant && variant.available === false;
      const soldOutText = (window.FoxTheme && FoxTheme.variantStrings && FoxTheme.variantStrings.soldOut) || 'Sold out';
      [this.querySelector('[data-atb-text]'), this.querySelector('[data-mh-sticky-submit] .mh-atb-left > span')].forEach(el => {
        if (!el) return;
        if (!el.dataset.defaultText) el.dataset.defaultText = el.textContent;
        if (soldOut) el.textContent = soldOutText;
        else if (el.textContent === soldOutText) el.textContent = el.dataset.defaultText;
      });
    }

    formatMoney(cents) {
      const c = Math.round(cents);
      const amount = (c / 100).toFixed(2);
      const noDec = Math.round(c / 100).toString();
      const fmt = this.moneyFormat || '${{amount}}';
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec);
    }

    escape(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  if (!customElements.get('item-builder')) {
    customElements.define('item-builder', ItemBuilder);
  }
})();